import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface TourContextValue {
  isActive: boolean;
  start: () => void;
  stop: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

// Per-user storage keys so a second account on the same browser sees a fresh
// tour (instead of inheriting the previous user's "completed" flag). The
// legacy global key is read once as a fallback so existing users don't get
// shown the tour again on the next deploy.
const LEGACY_STORAGE_KEY = 'telos-tour-completed';
const storageKey = (userId: string | null | undefined) =>
  userId ? `telos-tour-completed:${userId}` : LEGACY_STORAGE_KEY;
const FRESH_SIGNUP_KEY = 'telos-fresh-signup';
const AUTOSTART_DELAY_MS = 1500;

/** Has THIS user already seen (and dismissed/completed) the tour? */
function hasCompletedTour(userId: string | null | undefined): boolean {
  try {
    // Per-user key only — no fallback to the global legacy key, otherwise a
    // 2nd account on the same browser inherits the 1st user's completion.
    return localStorage.getItem(storageKey(userId)) === 'true';
  } catch {
    return false;
  }
}

/** SignUp happens on `telosintelligence.ai` (marketing); the dashboard /
 *  tour runs on `app.telosintelligence.ai`. localStorage is per-origin, so
 *  the flag set during signup is invisible by the time we reach the
 *  dashboard. We use a cookie scoped to `.telosintelligence.ai` so both
 *  subdomains see it. localStorage is kept as a fallback for dev/localhost
 *  (where the cookie's Domain attribute is ignored). */
function readCookie(name: string): string | null {
  try {
    const m = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
    return m ? decodeURIComponent(m.split('=').slice(1).join('=')) : null;
  } catch {
    return null;
  }
}
function writeCookie(name: string, value: string, maxAgeSec: number) {
  try {
    const isApex = typeof window !== 'undefined' && /telosintelligence\.ai$/.test(window.location.hostname);
    const dom = isApex ? '; Domain=.telosintelligence.ai' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSec}${dom}; SameSite=Lax`;
  } catch { /* ignore */ }
}
function deleteCookie(name: string) {
  try {
    const isApex = typeof window !== 'undefined' && /telosintelligence\.ai$/.test(window.location.hostname);
    const dom = isApex ? '; Domain=.telosintelligence.ai' : '';
    document.cookie = `${name}=; Path=/; Max-Age=0${dom}; SameSite=Lax`;
  } catch { /* ignore */ }
}

/** Public setter used by SignUp.tsx so the flag is correctly scoped to
 *  the .telosintelligence.ai parent domain (visible to both marketing
 *  and app subdomains). 10-minute TTL is plenty for the signup -> tour
 *  window; auto-expires so a stale flag can't haunt the next sign-in. */
export function markFreshSignup() {
  writeCookie(FRESH_SIGNUP_KEY, '1', 600);
  try { localStorage.setItem(FRESH_SIGNUP_KEY, '1'); } catch { /* ignore */ }
}

/** Did the user JUST sign up in this browser? Set by SignUp.tsx; cleared
 *  here when the tour starts. Cookie-first so it survives the cross-subdomain
 *  hop from marketing to app. */
function isFreshSignup(): boolean {
  if (readCookie(FRESH_SIGNUP_KEY) === '1') return true;
  try { return localStorage.getItem(FRESH_SIGNUP_KEY) === '1'; } catch { return false; }
}

function clearFreshSignup() {
  deleteCookie(FRESH_SIGNUP_KEY);
  try { localStorage.removeItem(FRESH_SIGNUP_KEY); } catch { /* ignore */ }
}

function markTourCompleted(userId: string | null | undefined) {
  try {
    localStorage.setItem(storageKey(userId), 'true');
  } catch {
    /* localStorage may be blocked — fail silently */
  }
  // Belt-and-braces: also clear the fresh-signup flag here so the tour
  // never re-fires on the next login, even if start() was bypassed.
  clearFreshSignup();
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);

  // Track which user-id we've already auto-considered, so we don't repeatedly
  // re-fire the timer on re-renders or when the user object identity changes.
  const handledUserRef = useRef<string | null>(null);

  const start = useCallback(() => {
    setIsActive(true);
  }, []);

  const stop = useCallback(() => {
    setIsActive(false);
    markTourCompleted(user?.id || user?.email || null);
  }, [user]);

  // Auto-start the tour for new users once they have a session AND have
  // landed on the dashboard. We gate by pathname so the timer doesn't fire
  // while the user is still on /onboarding (where the tour targets don't
  // exist yet) — the timer is (re)armed when they navigate to /dashboard.
  // Triggers after a 1.5s delay so the dashboard has time to render its targets.
  useEffect(() => {
    if (!user) return;
    if (hasCompletedTour(user?.id || user?.email)) return;
    if (!isFreshSignup()) return;

    // Don't consume the fresh-signup flag here. The effect can re-run when
    // AuthContext mutates the user object (e.g. RequirePaid->refreshUser)
    // and the cleanup will kill the pending timer; we need the flag to
    // survive so the next mount can re-arm. The flag is cleared inside the
    // timer callback below, right before the tour actually fires.
    // handledUserRef + the per-user tour-completed key prevent double-fires.

    // Only auto-start when the user is actually on the dashboard. If they're
    // still on /onboarding we wait for the next render after the route
    // changes — we listen for popstate + a custom 'telos:route-changed'
    // signal, but for the common case (Onboarding finishes → useNavigate
    // → dashboard mounts → TourProvider stays mounted higher in the tree)
    // we just re-check whenever the location changes.
    function onDashboard() {
      try {
        // App subdomain hosts the dashboard at /. Marketing host still uses /dashboard for legacy bookmarks (which redirect away). Treat both as 'on dashboard'.
        const path = window.location.pathname;
        const onAppHost = window.location.hostname === 'app.telosintelligence.ai' || window.location.hostname === 'localhost' || window.location.hostname.endsWith('.local');
        return (onAppHost && path === '/') || path.startsWith('/dashboard');
      } catch {
        return false;
      }
    }

    const userKey = user.id || user.email || 'unknown';

    function arm() {
      if (!onDashboard()) return null;
      if (handledUserRef.current === userKey) return null;
      handledUserRef.current = userKey;
      const id = window.setTimeout(() => {
        if (!hasCompletedTour(user?.id || user?.email) && onDashboard()) {
          clearFreshSignup();
          setIsActive(true);
        }
      }, AUTOSTART_DELAY_MS);
      return id;
    }

    let timerId: number | null = arm();

    // If we're not on /dashboard yet, watch for navigation and try again.
    // popstate covers back/forward; we also poll briefly to catch
    // react-router's pushState which doesn't fire a native event.
    let pollId: number | null = null;
    if (timerId == null && handledUserRef.current !== userKey) {
      const recheck = () => {
        if (handledUserRef.current === userKey) return;
        if (onDashboard()) {
          timerId = arm();
          if (pollId != null) {
            window.clearInterval(pollId);
            pollId = null;
          }
        }
      };
      window.addEventListener('popstate', recheck);
      pollId = window.setInterval(recheck, 250);

      return () => {
        window.removeEventListener('popstate', recheck);
        if (pollId != null) window.clearInterval(pollId);
        if (timerId != null) window.clearTimeout(timerId);
      };
    }

    return () => {
      if (timerId != null) window.clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <TourContext.Provider value={{ isActive, start, stop }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}
